import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import StrategyOverlay from "./components/StrategyOverlay";
import { loadRuntime } from "./lib/api";
import { apiBase } from "./lib/apiBase";

const CANONICAL_ENDPOINTS = {
    thinker: "/thinker",
    snapshotStatus: "/snapshot_status",
    mcpSnapshot: "/mcp_snapshot",
    thinkerThresholds: "/thinker_thresholds",
    snapshotList: "/snapshot_list",
    snapshotDiff: "/snapshot_diff",
};

function isObject(value) {
    return Boolean(value && typeof value === "object" && !Array.isArray(value));
}

function requireObject(value, label) {
    if (!isObject(value)) {
        throw new Error(`${label} returned malformed data`);
    }
    return value;
}

function normalizeSnapshotList(value) {
    const payload = requireObject(value, "Snapshot list");
    const rawIds = Array.isArray(payload.data)
        ? payload.data
        : Array.isArray(payload.snapshots)
            ? payload.snapshots.map((entry) => entry?.snapshot_id).filter(Boolean)
            : null;
    if (!Array.isArray(rawIds)) {
        throw new Error("Snapshot list returned malformed data");
    }
    return rawIds.filter((id) => typeof id === "string" && id.trim());
}

function normalizeThinkerThresholds(value) {
    const payload = requireObject(value, "Thinker thresholds");
    return isObject(payload.thresholds) ? payload.thresholds : payload;
}

function statusText(value) {
    if (value == null || value === "") return "unknown";
    if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
        return String(value);
    }
    return "reported";
}

function statusFromMetadata(payload, key) {
    if (!isObject(payload)) return "unknown";
    const value = payload[key];
    if (isObject(value)) {
        return statusText(value[key] ?? value.freshness_status ?? value.status);
    }
    return statusText(value);
}

function buildThinkerStatusItems(thinker, thresholds) {
    return [
        ["Thinker", statusText(thinker?.thinker_status ?? thinker?.status)],
        ["Score history", statusFromMetadata(thinker, "score_history_status")],
        ["Freshness", statusText(thinker?.score_history_status?.freshness_status)],
        ["Recommendation", statusText(thinker?.recommendation_status)],
        ["Threshold config", statusFromMetadata(thresholds, "threshold_override_status")],
        ["Threshold API", statusText(thresholds?.status)],
        ["Threshold score history", statusFromMetadata(thresholds, "score_history_status")],
        ["Influence", statusText(thresholds?.influence_mode ?? thinker?.influence_mode)],
    ];
}

function configuredCanonicalBase(cfg) {
    const rawBase = String(cfg?.apiBase ?? apiBase() ?? "").trim();
    return rawBase.replace(/\/+$/, "");
}

function canonicalApiUrl(path, cfg) {
    const canonicalPath = `/${String(path).replace(/^\/+/, "")}`;
    const base = configuredCanonicalBase(cfg);
    if (!base || base === "/") return canonicalPath;
    return `${base}${canonicalPath}`;
}

async function fetchCanonicalJson(path) {
    const cfg = await loadRuntime();
    const headers = new Headers();
    if (cfg.apiKey) headers.set("X-API-KEY", cfg.apiKey);
    if (cfg.authBearer) headers.set("Authorization", `Bearer ${cfg.authBearer}`);

    const response = await fetch(canonicalApiUrl(path, cfg), {
        credentials: "include",
        headers,
    });
    if (!response.ok) {
        throw new Error(`HTTP ${response.status} from ${path}`);
    }
    try {
        return await response.json();
    } catch {
        throw new Error(`${path} returned malformed JSON`);
    }
}

export default function SnapshotOverlay({ open, onClose }) {
    const [insights, setInsights] = useState([]);
    const [meta, setMeta] = useState(null);
    const [autoTuned, setAutoTuned] = useState(false);
    const [snapshots, setSnapshots] = useState([]);
    const [snapA, setSnapA] = useState("");
    const [snapB, setSnapB] = useState("");
    const [diff, setDiff] = useState({});
    const [loadState, setLoadState] = useState("idle");
    const [error, setError] = useState("");
    const [thinkerStatusItems, setThinkerStatusItems] = useState([]);

    useEffect(() => {
        if (!open) return undefined;
        let cancelled = false;

        async function loadMeta() {
            setLoadState("loading");
            setError("");
            setInsights([]);
            setMeta(null);
            setAutoTuned(false);
            setSnapshots([]);
            setSnapA("");
            setSnapB("");
            setDiff({});
            setThinkerStatusItems([]);

            try {
                const [thinkerRes, statusRes, snapRes, threshRes, snapList] =
                    await Promise.all([
                        fetchCanonicalJson(CANONICAL_ENDPOINTS.thinker),
                        fetchCanonicalJson(CANONICAL_ENDPOINTS.snapshotStatus),
                        fetchCanonicalJson(CANONICAL_ENDPOINTS.mcpSnapshot),
                        fetchCanonicalJson(CANONICAL_ENDPOINTS.thinkerThresholds),
                        fetchCanonicalJson(CANONICAL_ENDPOINTS.snapshotList),
                    ]);

                const thinker = requireObject(thinkerRes, "Thinker");
                if (!Array.isArray(thinker.insights)) {
                    throw new Error("Thinker returned malformed data");
                }

                const status = requireObject(statusRes, "Snapshot status");
                if (typeof status.timestamp !== "string" || !status.timestamp) {
                    throw new Error("Snapshot status returned malformed data");
                }

                const snapshot = requireObject(snapRes, "MCP snapshot");
                if (snapshot.snapshot_schema_version == null) {
                    throw new Error("MCP snapshot returned malformed data");
                }

                const thresholds = normalizeThinkerThresholds(threshRes);
                const ordered = normalizeSnapshotList(snapList);
                const today = new Date().toISOString().slice(0, 10);
                const auto = Object.values(thresholds).some((t) =>
                    String(t?.updated_at || t?.timestamp || "").startsWith(today),
                );

                if (cancelled) return;
                setInsights(thinker.insights);
                setMeta({
                    timestamp: status.timestamp,
                    version: snapshot.snapshot_schema_version,
                });
                setAutoTuned(auto);
                setThinkerStatusItems(buildThinkerStatusItems(thinker, threshRes));
                setSnapshots(ordered);
                setSnapA(ordered[0] || "");
                setSnapB(ordered[1] || ordered[0] || "");
                setLoadState(ordered.length > 0 ? "ready" : "empty");
            } catch (err) {
                if (cancelled) return;
                setInsights([]);
                setMeta(null);
                setAutoTuned(false);
                setSnapshots([]);
                setSnapA("");
                setSnapB("");
                setDiff({});
                setThinkerStatusItems([
                    ["Thinker", "unavailable"],
                    ["Score history", "unknown"],
                    ["Freshness", "unknown"],
                    ["Recommendation", "unknown"],
                    ["Threshold config", "unknown"],
                    ["Threshold API", "unavailable"],
                    ["Threshold score history", "unknown"],
                    ["Influence", "unknown"],
                ]);
                setError(err?.message || "Snapshot data unavailable");
                setLoadState("error");
            }
        }

        loadMeta();
        return () => {
            cancelled = true;
        };
    }, [open]);

    useEffect(() => {
        if (!snapA || !snapB) {
            setDiff({});
            return undefined;
        }
        let cancelled = false;
        async function loadDiff() {
            try {
                const params = new URLSearchParams({ a: snapA, b: snapB });
                const data = await fetchCanonicalJson(
                    `${CANONICAL_ENDPOINTS.snapshotDiff}?${params.toString()}`,
                );
                const payload = requireObject(data, "Snapshot diff");
                const normalized = isObject(payload.diff) ? payload.diff : payload;
                if (!cancelled) setDiff(normalized);
            } catch {
                if (!cancelled) setDiff({});
            }
        }
        loadDiff();
        return () => {
            cancelled = true;
        };
    }, [snapA, snapB]);

    const renderSection = (section, entries) => {
        if (!isObject(entries)) return null;
        return (
            <div key={section} className="mb-4">
                <h4 className="font-semibold mb-1">{section}</h4>
                <table className="w-full text-xs text-left">
                    <thead>
                        <tr>
                            <th>Coin</th>
                            <th>From</th>
                            <th>To</th>
                            <th>Δ%</th>
                        </tr>
                    </thead>
                    <tbody>
                        {Object.entries(entries).map(([coin, v]) => (
                            <tr
                                key={coin}
                                className={
                                    Math.abs(v.pct_change || 0) > 5
                                        ? "text-yellow-300"
                                        : ""
                                }
                            >
                                <td>{coin}</td>
                                <td>{v.from}</td>
                                <td>{v.to}</td>
                                <td>
                                    {v.pct_change != null ? v.pct_change.toFixed(2) : "-"}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    };

    const hasDiff = Object.keys(diff).length > 0;

    return (
        <StrategyOverlay open={open} onClose={onClose} title="Snapshot View">
            {loadState === "loading" && (
                <div className="text-xs text-gray-400 mb-2" role="status">
                    Loading snapshot data…
                </div>
            )}
            {loadState === "error" && (
                <div className="text-xs text-red-300 mb-2" role="alert">
                    Snapshot data unavailable: {error}
                </div>
            )}
            {meta && loadState !== "error" && (
                <div className="text-xs text-gray-400 mb-2">
                    Snapshot: {meta.timestamp} (schema {meta.version})
                    {autoTuned && (
                        <span
                            className="ml-1 text-yellow-400"
                            title="Thinker thresholds auto-tuned today"
                        >
                            ★
                        </span>
                    )}
                </div>
            )}
            <details className="mb-2">
                <summary className="cursor-pointer">Thinker Insights</summary>
                <ul className="list-disc ml-5 text-sm">
                    {loadState === "loading" && <li>Loading insights…</li>}
                    {loadState === "error" && <li>Insights unavailable</li>}
                    {loadState !== "loading" && loadState !== "error" && insights.length === 0 && (
                        <li>No insights</li>
                    )}
                    {loadState !== "error" && insights.slice(0, 5).map((i, idx) => (
                        <li key={idx}>{String(i)}</li>
                    ))}
                </ul>
                {thinkerStatusItems.length > 0 && (
                    <div
                        className="mt-2 flex flex-wrap gap-1 text-xs"
                        aria-label="Thinker status taxonomy"
                    >
                        {thinkerStatusItems.map(([label, value]) => (
                            <span
                                key={label}
                                className="rounded border border-gray-600 bg-gray-800 px-2 py-0.5 text-gray-200"
                                title={`${label}: ${value || "unknown"}`}
                            >
                                {label}: {value || "unknown"}
                            </span>
                        ))}
                    </div>
                )}
            </details>
            {loadState === "empty" && (
                <div className="text-xs text-gray-400 mb-3">No snapshots available.</div>
            )}
            {snapshots.length > 0 && loadState !== "error" && (
                <div className="flex gap-2 mb-3 text-xs">
                    <select
                        value={snapA}
                        onChange={(e) => setSnapA(e.target.value)}
                        className="bg-gray-800 rounded"
                    >
                        {snapshots.map((s) => (
                            <option key={s} value={s}>
                                {s}
                            </option>
                        ))}
                    </select>
                    <span className="self-center">vs</span>
                    <select
                        value={snapB}
                        onChange={(e) => setSnapB(e.target.value)}
                        className="bg-gray-800 rounded"
                    >
                        {snapshots.map((s) => (
                            <option key={s} value={s}>
                                {s}
                            </option>
                        ))}
                    </select>
                    {hasDiff && (
                        <button
                            type="button"
                            className="ml-auto underline"
                            onClick={() => {
                                const blob = new Blob([
                                    JSON.stringify(diff, null, 2),
                                ], { type: "application/json" });
                                const url = URL.createObjectURL(blob);
                                const a = document.createElement("a");
                                a.href = url;
                                a.download = "snapshot_diff.json";
                                a.click();
                                URL.revokeObjectURL(url);
                            }}
                        >
                            Download
                        </button>
                    )}
                </div>
            )}
            <div className="max-h-72 overflow-y-auto space-y-4 text-sm">
                {!hasDiff && loadState !== "loading" && loadState !== "error" && (
                    <div className="text-center text-gray-400">No snapshot diff data</div>
                )}
                {hasDiff && Object.entries(diff).map(([section, entries]) =>
                    renderSection(section, entries),
                )}
            </div>
        </StrategyOverlay>
    );
}

SnapshotOverlay.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
};
